import { getLotteryModalityByValue } from '../../../constants/lotteryModalidades';
import { enrichNodeFilters } from './filterParameters';
import {
    getNodeTypeOption,
    getNodeTypeParameterDefinitions,
    getNodeTypeStepLinks,
} from '../../../constants/nodeTypeParameters';

export interface WorkflowNode {
    id: number;
    type?: number;
    title?: string;
    parameters?: Record<string, unknown>;
    filters?: Array<{ name: string; parameters: Record<string, unknown> }>;
    from?: number[] | number;
    fromListId?: number | null;
    toListId?: number | null;
}

export interface ExecutionStep {
    id: number;
    type: number;
    parameters: Record<string, unknown>;
    dependencies: number[];
    percent: number;
    running: boolean;
    status: boolean;
}

const normalizeId = (value: unknown): number | null => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
};

export const getUpstreamNodeIds = (node: WorkflowNode): number[] => {
    if (Array.isArray(node.from)) {
        return node.from
            .map(normalizeId)
            .filter((item): item is number => item !== null);
    }

    if (node.from !== undefined && node.from !== null) {
        const single = normalizeId(node.from);
        return single !== null ? [single] : [];
    }

    return [];
};

const getNodeFilters = (node: WorkflowNode) => {
    if (Array.isArray(node.filters)) {
        return node.filters;
    }

    if (Array.isArray(node.parameters?.filters)) {
        return node.parameters.filters as Array<{ name: string; parameters: Record<string, unknown> }>;
    }

    return [];
};

const applyStepLinkParameters = (
    parameters: Record<string, unknown>,
    node: WorkflowNode
) => {
    const links = getNodeTypeStepLinks(node.type ?? 1);
    const fromListId = normalizeId(node.fromListId);
    const toListId = normalizeId(node.toListId);

    if (links.usesFromList && fromListId !== null) {
        parameters.from = fromListId;
    }

    if (links.usesToList && toListId !== null) {
        parameters.to = toListId;
    }
};

export const convertNodesToExecutionSteps = (
    nodes: WorkflowNode[],
    modality: string
): { steps: ExecutionStep[] } => {
    const qtdTot = getLotteryModalityByValue(modality)?.qty_disp ?? 60;

    const steps = nodes.map((node) => {
        const type = node.type ?? 1;
        const typeOption = getNodeTypeOption(type);
        const parameters: Record<string, unknown> = {};

        applyStepLinkParameters(parameters, node);
        parameters.filters = typeOption.filter
            ? enrichNodeFilters(getNodeFilters(node), modality)
            : [];

        if (type === 1) {
            parameters.qtd_tot = qtdTot;
        }

        if (typeOption.params) {
            getNodeTypeParameterDefinitions(type).forEach((definition) => {
                const value = node.parameters?.[definition.key];
                if (value !== undefined && value !== null && value !== '') {
                    parameters[definition.key] = value;
                }
            });
        }

        return {
            id: node.id,
            type,
            parameters,
            dependencies: getUpstreamNodeIds(node),
            percent: 0,
            running: false,
            status: false,
        };
    });

    return { steps };
};
